<template>
    <!-- `persistent` is kept on purpose: a stray backdrop click must not answer
         a destructive question for the user. It does mean Vuetify stops
         handling Esc for us, so Esc is wired back — but from a document-level
         listener, NOT @keydown.esc here. A handler on the dialog only fires
         when focus is already inside it, and after clicking a non-focusable
         action icon (the delete icon on Projects is a <div>) focus is still on
         <body>, so the key never reached the dialog. That is why Esc did
         nothing. See onDocumentKeydown().

         `data-esc-close="off"` stands this dialog down from the app-wide Esc
         handler (plugins/escCloseDialogs.ts). It has a condition the generic
         handler cannot know about: this dialog owes its caller a PROMISE, and
         closing it without resolving that promise leaves every
         `await this.$refs.confirm.open(...)` hanging forever. -->
    <v-dialog
        v-model="dialog"
        data-esc-close="off"
        :max-width="options.width"
        :style="{ zIndex: options.zIndex }"
        persistent
    >
        <v-card>
            <v-toolbar dark color="primary" dense flat>
                <v-toolbar-title class="text-body-5 font-weight-bold">{{ title }}</v-toolbar-title>
            </v-toolbar>
            <v-card-text v-show="!!message" class="text-center" v-html="message"></v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn v-if="!options.noconfirm" color="error" text class="body-2 font-weight-bold" @click.native="cancel">Back</v-btn>
                <v-btn color="info" class="body-2 font-weight-bold" @click.native="agree">OK</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>
<script>
export default {
    data() {
        return {
            dialog: false,
            resolve: null,
            reject: null,
            message: null,
            title: null,
            options: {
                color: 'grey lighten-3',
                width: 400,
                zIndex: 5000,
                noconfirm: false
            }
        };
    },

    mounted() {
        // Document level, not the dialog: see the template note. Bound for the
        // component's whole life rather than toggled with `dialog`, because a
        // listener added while the dialog opens can miss a key pressed in the
        // same tick; the handler is a no-op when closed anyway.
        document.addEventListener('keydown', this.onDocumentKeydown);
    },

    beforeUnmount() {
        document.removeEventListener('keydown', this.onDocumentKeydown);
    },

    methods: {
        open(title, message, options) {
            this.dialog = true;
            this.title = title;
            this.message = message;
            this.options = Object.assign(this.options, options);
            return new Promise((resolve, reject) => {
                this.resolve = resolve;
                this.reject = reject;
            });
        },

        onDocumentKeydown(event) {
            if (event.key !== 'Escape' || !this.dialog) return;

            // Let a nested overlay take Esc first — a v-menu or select renders
            // outside this dialog's DOM, so without this one Esc would answer
            // the confirmation when the user only meant to shut a dropdown.
            if (document.querySelector('.v-menu.v-overlay--active')) return;

            event.stopPropagation();
            this.cancel();
        },

        agree() {
            this.settle(true);
        },

        cancel() {
            this.settle(false);
        },

        /**
         * Close once and answer the caller exactly once.
         *
         * `resolve` is cleared as it is used. Esc, the Back button and a second
         * click on OK can all arrive for the same question, and calling a
         * settled promise's resolve again is a silent no-op that hides the
         * double-answer; clearing it makes the second call provably do nothing.
         */
        settle(answer) {
            const resolve = this.resolve;
            this.resolve = null;
            this.reject = null;
            this.dialog = false;
            if (resolve) resolve(answer);
        }
    }
};

// <confirm-dialog ref="confirm"></confirm-dialog>
// async buttonfunction(){
//     if(await this.$refs.confirm.open("Confirm", "Are you sure you want to delete all record?")){
//         console.log("TEST");
//     }
// },
</script>
